import { InsuranceReportApiTypes } from '@frontend/api-insurance-eligibility-report';
import {
  TableFilterKey,
  TablesDataType,
  Vendors,
} from '../dental-insurance-eligibility-report/dental-insurance-eligibility-report/types/types';
import { Benefit, BenefitLimit, TuuthFairyDentalSummaryResponse } from '../types/tuuthfairy-types';

export const toTitleCase = (str: string) => {
  if (!str) return '';

  return str
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const formatBenefitLimitData = (benefitLimit: BenefitLimit[]) => {
  return benefitLimit.map((benefit) => ({
    ...benefit,
    amount: (benefit.amount || 0) / 100,
    remaining: (benefit.remaining || 0) / 100,
    networkType: toTitleCase(benefit.networkType),
    coverageLevel: toTitleCase(benefit.coverageLevel),
    category: toTitleCase(benefit.category),
  }));
};

const formatBenefitData = (benefits: Benefit[]) => {
  return benefits.map((benefit) => ({
    ...benefit,
    networkType: toTitleCase(benefit.networkType),
    category: toTitleCase(benefit.category),
  }));
};

const tuuthFairyDataFormattedForTables = (tuuthfairyDataRes: TuuthFairyDentalSummaryResponse) => {
  const tablesData: TablesDataType = {};
  tablesData.maximum = formatBenefitLimitData(
    tuuthfairyDataRes?.dentalSummaryInfo?.plan?.maximums
  ) as TablesDataType['maximum'];
  tablesData.deductible = formatBenefitLimitData(
    tuuthfairyDataRes?.dentalSummaryInfo?.plan?.deductibles
  ) as TablesDataType['deductible'];
  //limitation in dentalXChange is called as benefits in tuuthfairy using the same limitation property so that users will be familiar
  tablesData.limitation = formatBenefitData(tuuthfairyDataRes?.dentalSummaryInfo?.plan?.benefits);
  return tablesData;
};

const dentalXChangeDataFormattedForTables = (
  dentalXchangeDataRes: InsuranceReportApiTypes.DentalXChangeDentalSummaryResponse
) => {
  const tablesData: TablesDataType = dentalXchangeDataRes?.dentalSummaryInfo;
  return tablesData;
};

export const formattedDentalCoverageResponse = (
  dentalCoverageRes: InsuranceReportApiTypes.InsuranceCoverageResponseDental | undefined,
  vendor: Vendors
) => {
  if (!dentalCoverageRes) return {};
  switch (vendor) {
    case Vendors.DentalXChange:
      return dentalXChangeDataFormattedForTables(
        dentalCoverageRes as InsuranceReportApiTypes.DentalXChangeDentalSummaryResponse
      );
    case Vendors.TuuthFairy:
      return tuuthFairyDataFormattedForTables(dentalCoverageRes as TuuthFairyDentalSummaryResponse);
    default:
      throw new Error('Vendor not supported for table view');
  }
};

//This function helps in fetching network key from the object which contains in-network/out-of-network values
export const getNetworkKeyForVendor = (vendor: Vendors | null): TableFilterKey => {
  switch (vendor) {
    case Vendors.DentalXChange:
      return 'network';
    case Vendors.TuuthFairy:
      return 'networkType';
    default:
      throw new Error('Unsupported vendor for network key');
  }
};

export const NETWORK_KEY_MAPPINGS_BY_VENDOR = {
  DentalXChange: {
    'in-network': 'In-Network', // In DXC for network key value is of 'In-Network'
    'out-of-network': 'Out-Of-Network',
    networkName: null, //DXC just have network type not network names like Delta dental ppo, premiere
  },
  TuuthFairy: {
    'in-network': 'in_network', //In Tuuthfairy for networkType key value is of 'in_network'
    'out-of-network': 'out_of_network',
    networkName: 'networkName',
  },
};

export const isInNetwork = (networkType: string, vendor: Vendors) => {
  switch (vendor) {
    case Vendors.DentalXChange:
      return networkType === NETWORK_KEY_MAPPINGS_BY_VENDOR.DentalXChange['in-network'];

    case Vendors.TuuthFairy:
      return networkType === NETWORK_KEY_MAPPINGS_BY_VENDOR.TuuthFairy['in-network'];

    default:
      return false;
  }
};
